/**
 * Create an issue. Used from the list and from a contact's profile.
 *
 * `contactId` is optional and that is deliberate — «سرور دیروز کند بود» is an
 * internal problem nobody reported from outside, and inventing a contact for it
 * would be worse than admitting it has none. The feedback service allows a NULL
 * contact for the same reason.
 */
import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import {
  FormDialog,
  SelectField,
  TextArea,
  TextField,
} from '../FormKit.web.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';
import {
  ISSUE_KIND_LABELS,
  ISSUE_PRIORITY_LABELS,
} from '../../lib/issueLabels.js';
import { CREATE_ISSUE, CREATE_ISSUE_TYPE, ISSUE_TYPES_QUERY } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';

export function IssueDialog({
  open,
  onOpenChange,
  contactId = null,
  linkTargetType = null,
  linkTargetId = null,
  onCreated,
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('PROBLEM');
  const [priority, setPriority] = useState('MEDIUM');
  const [typeId, setTypeId] = useState('');

  const { isAdmin } = useSession();
  const { data, loading: typesLoading } = useQuery(ISSUE_TYPES_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  const [create, { loading, error }] = useMutation(CREATE_ISSUE);
  // `typeId` is REQUIRED by the feedback service, so an unseeded ISSUE_TYPE
  // vocabulary makes «ثبت مسئله» unusable — the same dead end as the calendar's.
  const [createType, createTypeState] = useMutation(CREATE_ISSUE_TYPE, {
    refetchQueries: ['IssueTypes'],
    awaitRefetchQueries: true,
  });
  const types = data?.issueTypes ?? [];

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.issues.newIssue}
      submitLabel={S.app.create}
      busy={loading}
      error={error}
      canSubmit={Boolean(title.trim() && (typeId || types[0]?.id))}
      onSubmit={async () => {
        const created = await create({
          variables: {
            kind,
            typeId: typeId || types[0]?.id,
            title: title.trim(),
            description: description.trim() || null,
            contactId,
            priority,
            linkTargetType,
            linkTargetId,
          },
        });
        setTitle('');
        setDescription('');
        onOpenChange(false);
        onCreated?.(created.data?.createIssue);
      }}
    >
      <TextField
        label={S.issues.issueTitle}
        value={title}
        onChange={(event) => setTitle(event.target.value)}
      />
      <TextArea
        label={S.issues.description}
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <SelectField
        label={S.issues.kind}
        value={kind}
        onChange={(event) => setKind(event.target.value)}
      >
        {Object.entries(ISSUE_KIND_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </SelectField>
      <VocabularyField
        label={S.issues.type}
        value={typeId}
        onChange={setTypeId}
        options={types}
        loading={typesLoading}
        required
        canCreate={isAdmin}
        codePrefix="ISSUE"
        creating={createTypeState.loading}
        createError={createTypeState.error}
        onCreate={async ({ code, labelFa }) => {
          const created = await createType({ variables: { code, labelFa } });
          return created.data?.createIssueType;
        }}
      />
      <SelectField
        label={S.issues.priority}
        value={priority}
        onChange={(event) => setPriority(event.target.value)}
      >
        {Object.entries(ISSUE_PRIORITY_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </SelectField>
    </FormDialog>
  );
}
